import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FormSpy } from 'react-final-form';

import {
  FormInput,
  FormLabel,
  FormSelect,
  FormIconChooser,
  FormRadio,
  FormDateInput,
  BaseForm,
  FormInputHolder,
} from './form';

import { Button, SecondaryButton } from './buttons';
import { required } from '../helpers/formValidators';
import babyIcons from './icons/babyIcons';
import './BabyEdit.scss';

function BabyEdit({
  onSubmit,
  onAddAnotherClicked,
  onCancelClicked,
  onNameChange,
  hideAddAnother = false,
  error = null,
  initialValues = {}
}) {
  const { t } = useTranslation();
  const { colorScheme } = initialValues;

  return (
    <BaseForm
      className="baby-edit"
      onSubmit={(values) => {
        return onSubmit(values);
      }}
      initialValues={initialValues}
    >
      <FormSpy
        subscription={{ values: true }}
        onChange={(state) => {
          setTimeout(() => {
            if (onNameChange) onNameChange(state.values.name);
          }, 0);
        }}
      />

      <h2>{t('baby.formTitle', 'Baby Information Form')}</h2>

      <div className="baby-edit__row">
        <div className="baby-edit__field">
          <FormInput
            labelRenderer={() => <FormLabel>{t('baby.name')}</FormLabel>}
            name="name"
            validate={required}
          />
        </div>
        <div className="baby-edit__field">
          <FormLabel>{t('baby.sex')}</FormLabel>
          <FormSelect
            options={[
              { value: 'M', label: t('baby.male') },
              { value: 'F', label: t('baby.female') },
              { value: 'O', label: t('baby.other') }
            ]}
            name="sex"
            validate={required}
          />
        </div>
      </div>

      <div className="baby-edit__row">
        <div className="baby-edit__field">
          <FormDateInput
            labelRenderer={() => <FormLabel>{t('baby.birthDate')}</FormLabel>}
            name="birthDate"
            validate={required}
          />
        </div>
        <div className="baby-edit__field">
          <FormDateInput
            labelRenderer={() => <FormLabel>{t('baby.dueDate')}</FormLabel>}
            name="dueDate"
            validate={required}
          />
        </div>
        <div className="baby-edit__field">
          <FormDateInput
            labelRenderer={() => (
              <FormLabel note={t('baby.optional')}>{t('baby.admissionDate')}</FormLabel>
            )}
            name="admissionDate"
          />
        </div>
      </div>

      <div className="baby-edit__row">
        <div className="baby-edit__field">
          <FormInput
            labelRenderer={() => (
              <FormLabel note={t('baby.optional')}>{t('baby.deliveryType')}</FormLabel>
            )}
            name="deliveryType"
          />
        </div>
        <div className="baby-edit__field">
          <FormInput
            labelRenderer={() => <FormLabel note={t('baby.optional')}>{t('baby.diagnosis')}</FormLabel>}
            name="diagnosis"
          />
        </div>
        <div className="baby-edit__field">
          <FormInput
            labelRenderer={() => (
              <FormLabel note={t('baby.optional')}>{t('baby.room')}</FormLabel>
            )}
            name="room"
          />
        </div>
      </div>

      <div className="baby-edit__select-icon-label">
        <FormLabel>{t('baby.selectAnIcon')}</FormLabel>
      </div>

      <FormIconChooser>
        {colorScheme && [1, 2, 3, 4].map((number, index) => (
          <FormRadio
            key={number}
            name="icon"
            value={`${colorScheme}${number}`}
            imageSrc={babyIcons[`${colorScheme}${number}`]}
            validate={index === 0 ? required : undefined}
          />
        ))}
      </FormIconChooser>

      <div className="baby-edit__error-container">
        {error && <p className="error-text">{t(error)}</p>}
      </div>

      <FormSpy subscription={{ submitting: true, valid: true, values: true }}>
        {({ submitting, valid, values }) => {
          const isDisabled = submitting || !valid || !values.icon;
          return (
            <FormInputHolder>
              {!hideAddAnother && (
                <SecondaryButton
                  loading={submitting}
                  disabled={submitting}
                  colorScheme="blue"
                  label={t('baby.addAnotherBaby')}
                  onClick={() => {
                    onAddAnotherClicked();
                  }}
                  type="button" 
                />
              )}
              <Button
                loading={submitting}
                disabled={isDisabled}
                colorScheme="red"
                label={t('baby.saveContinue')}
                className="last"
                type="submit"
              />
            </FormInputHolder>
          );
        }}
      </FormSpy>
    </BaseForm>
  );
}

BabyEdit.propTypes = {
  initialValues: PropTypes.shape({
    colorScheme: PropTypes.string.isRequired
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onAddAnotherClicked: PropTypes.func.isRequired,
  onCancelClicked: PropTypes.func,
  onNameChange: PropTypes.func,
  error: PropTypes.string
};

export default BabyEdit;
